import { AnyObject } from '../../../Types';
import View, { ViewLocals } from './view';
import { ContentWidgetDialogAction } from '../../PandoraSolutionBuilderAgent/model/enumerations';
import type { MxAction } from './action';
import type { IDeferred } from 'angular';

export type DetailViewContext = AnyObject;

export type DetailsViewLocals = ViewLocals & {
	context: DetailViewContext;
};

/**
 * @ngdoc type
 * @name mx.shell.DetailsView
 * @module mx.shell
 *
 * @description
 * An implementation of the Details View for {@ref mx.shell.SidePanelService SidePanelService}.<br>
 * <b>Inherited from {@ref mx.shell.View mx.shell.View}.</b><br>
 * A constructor accepts `options` parameter that may contain fields to extend corresponding properties. Created like:
 * ```javascript
 var options = {
	id: 'user-details-test',
	type: 'user',
	name: 'Markus, Schmidt',
	displayName: 'User: Markus, Schmidt',
	context: vm.selectedItemContext,
	locals: {
		incidentContext: vm.selectedItemContext,
		userContext: {firstName: 'Markus', lastName: 'Schmidt'}
	},
	uiActionGroup: 'user_view',
 };

 var view = new mx.shell.DetailsView(options);
 * ```
 *
 * @property {string} uiActionGroup A group identifier which allows to connect this `DetailsView` with `uiActions`
 * from {@ref mx.shell.SidePanelService SidePanelService}
 * @property {string} correspondingUrl - Url of the corresponding View/Page that can be opened by coping this URL into another browser's tab.
 * This Url doesn't define current state of the application, but allows just get current tab/view/content on a separate page.
 * Is used to close all children DetailsViews automatically when parent DetailsView is closed
 * @property {boolean} visible — shows if details view is currently visible
 * @property {boolean} dirty - True if user has already interacted with the form.
 * @property {boolean} merge - True if user has already interacted with the form.
 * @property {boolean} hideActions - True for hide actions.
 */
export default class DetailsView extends View<DetailsViewLocals> {
	public uiActionGroup: string;
	public correspondingUrl: string;
	public hideActions = false;
	public visible = true; // OBSOLETE ?
	public dirty = false;
	public mergeDisabled = false;
	public context: DetailViewContext;
	public dialogType?: ContentWidgetDialogAction;
	public startedFromAction?: MxAction;
	public finishDefer?: IDeferred<unknown>;

	public headerTitle?: string;
	public widgetId?: string;
	public widgetName?: string;
	public telemetrySource?: string;

	constructor(options: Partial<DetailsView> = {}) {
		super(options);
		this.headerTitle = options.headerTitle;
		this.widgetId = options.widgetId;
		this.widgetName = options.widgetName;
		this.telemetrySource = options.telemetrySource;
		this.uiActionGroup = options.uiActionGroup || '';
		this.correspondingUrl = options.correspondingUrl || '';
		this.hideActions = options.hideActions || false;
		this.visible = typeof options.visible === 'boolean' ? options.visible : true;
		this.dirty = typeof options.dirty === 'boolean' ? options.dirty : false;
		this.mergeDisabled = typeof options.mergeDisabled === 'boolean' ? options.mergeDisabled : false;
		this.dialogType = options.dialogType;

		this.context = options.context || {};
		this.locals.context = options.context || {};

		// If `correspondingUrl` is not set and an context is set then lets assume that it's `Object Details Page`
		if (!this.correspondingUrl && this.context && this.context._type && this.context._id) {
			this.correspondingUrl = '/object-details/' + this.context._type + '/' + this.context._id;
		}
	}
}
